import { Box, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import * as React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CurrencyField } from '~/components/GenericForm/CurrencyField';
import { BackDatingNotification } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import {
  SalaryProposalFormData,
  SalaryProposalHourlyComponentFormData,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/SalaryProposalForm/data';
import { SalaryProposalFieldProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposals/components/EmployeeSalaryProposalsLayout/useSalaryProposalsLayout';

import { useSxStyles } from './useStyles';

type PropsType = {
  form: UseFormReturn<SalaryProposalFormData>;
  currency: string;
  disabled?: boolean;
  index: number;
  fieldProps: SalaryProposalFieldProps;
};

type HourlyComponentFields = Omit<
  SalaryProposalHourlyComponentFormData,
  'hourlySalaryEnum'
>;

export const HourlySalaryComponent = (props: PropsType) => {
  const {
    form: {
      getValues,
      control,
      watch,
      trigger,
      formState: { isSubmitted },
      getFieldState,
    },
    index,
    disabled,
    currency,
    fieldProps,
  } = props;
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const fieldArrayName: keyof SalaryProposalFormData = 'hourlySalaryComponents';

  const componentName = useMemo(() => {
    return getValues(`${fieldArrayName}.${index}.hourlySalaryEnum.name`) || '';
  }, [getValues, fieldArrayName, index]);

  const { fieldNames, triggerPaths } = useMemo(() => {
    const getFieldName = <K extends keyof HourlyComponentFields>(
      fieldName: K,
    ): `hourlySalaryComponents.${number}.${K}` =>
      `${fieldArrayName}.${index}.${fieldName}`;

    const fieldNames = {
      startDate: getFieldName('startDate'),
      amount: getFieldName('amount'),
    };

    return {
      fieldNames,
      triggerPaths: Object.values(fieldNames),
    };
  }, [fieldArrayName, index]);

  const startDateField = {
    value: watch(fieldNames.startDate),
    fieldState: getFieldState(fieldNames.startDate),
  };
  const amountField = {
    value: watch(fieldNames.amount),
  };

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const throttledTrigger = () => {
    timeoutRef.current = setTimeout(() => {
      trigger(triggerPaths);
    }, 1000);
  };

  const onChangeTrigger =
    <T,>(onChange: (...event: T[]) => void) =>
    (...event: T[]) => {
      onChange(...event);
      if (isSubmitted) {
        throttledTrigger();
      }
    };

  return (
    <Box key={`hourlySalaryComponent${index}`} sx={sxStyles.hourlyComponent}>
      {componentName}
      <Box sx={sxStyles.fieldRow}>
        <Controller
          control={control}
          name={fieldNames.startDate}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <DatePicker
                label={t('laStartDate')}
                value={value}
                onChange={onChangeTrigger(onChange)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required={!!amountField.value}
                    error={!!error?.message}
                    helperText={error?.message}
                  />
                )}
                disabled={disabled}
                minDate={fieldProps.datePickers.startDate.minDate}
              />
            );
          }}
        />

        <Controller
          control={control}
          name={fieldNames.amount}
          render={({ field: { value, onChange }, fieldState: { error } }) => {
            return (
              <CurrencyField
                label={t('laAmount')}
                value={value}
                onChange={onChangeTrigger(onChange)}
                currency={currency}
                disabled={disabled}
                error={!!error?.message}
                helperText={error?.message}
              />
            );
          }}
        />
      </Box>
      <BackDatingNotification
        enabled={startDateField.fieldState.isDirty}
        startDate={startDateField.value}
      />
    </Box>
  );
};
